import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  api,
  ErrorApi,
  type CampoDelCotejo,
  type Estado,
  type Exposicion,
  type FilaHistorial,
  type LadoDeDocumento,
  type Resumen,
  type SenalesDeExpediente,
  type Verificacion,
} from './api';
import { AvisoError, Avatar, Boton, Pastilla, Skeleton, Tarjeta, Vacio } from './ui';

/**
 * Diseño 5a — el command center.
 *
 * Riel de tinta a la izquierda (identidad fija), área clara al centro (el trabajo), panel de
 * perfil a la derecha (el detalle de una persona).
 *
 * ── LA DECISIÓN QUE MÁS IMPORTA DE ESTA PANTALLA ────────────────────────────────
 *
 * La tabla va AGRUPADA POR VEREDICTO, con "A revisión" primero. Siempre.
 *
 * No es estético. Los casos en revisión son los únicos que exigen que alguien haga algo hoy;
 * los aprobados son ruido resuelto. Una lista plana ordenada por fecha entierra lo urgente
 * entre lo terminado, y a las 8 de la mañana eso significa que alguien no revisó lo que
 * tenía que revisar.
 *
 * ── LO QUE NO EXISTE AQUÍ ───────────────────────────────────────────────────────
 *
 * No hay columna de motivo. No hay score. No hay barra de progreso. No hay semáforo con
 * número. Si el perfil de exposición del cliente lo permite, el servidor manda `motivos` o
 * `riesgo`; si no los manda, no existen en el objeto y no hay nada que esconder.
 *
 * El EXPEDIENTE (señales, lecturas del documento, cotejo, vida) sigue la misma regla: el
 * servidor solo lo manda al rol ADMIN, y esta pantalla lo pinta únicamente si llegó. El
 * front no pregunta "¿soy admin?": dibuja lo que hay en el objeto.
 */

const ORDEN: Estado[] = ['revision', 'rechazado', 'aprobado'];

const GRUPO: Record<Estado, { label: string; hint: string }> = {
  revision: { label: 'A revisión', hint: 'esperan a una persona' },
  rechazado: { label: 'Rechazados', hint: '' },
  aprobado: { label: 'Aprobados', hint: '' },
};

const TEXTO_EXPOSICION: Record<Exposicion, string> = {
  VERDICT_ONLY: 'solo veredicto',
  VERDICT_AND_RULE: 'veredicto y regla',
  RISK_LEVEL: 'nivel de riesgo',
  FILE: 'expediente',
};

function fecha(iso: string): string {
  return new Date(iso).toLocaleString('es-PE', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── El expediente (solo si el servidor lo mandó — rol ADMIN) ─────────────────────
//
// Las claves de OCR/MRZ vienen del parser de face-match (omnia, apps/face-match/main.py);
// las etiquetas en español son las impresas en el DNI peruano. Lo que llegue y estas
// listas no contemplen NO se oculta: cae al <pre> crudo. Ocultar una señal nueva por
// tener la lista vieja sería mentirle justamente a quien audita.

const ETIQUETA_OCR: Record<string, string> = {
  dni: 'Número',
  cui: 'CUI',
  primer_apellido: 'Primer apellido',
  segundo_apellido: 'Segundo apellido',
  nombres: 'Prenombres',
  birth_date: 'Fecha de nacimiento',
  fecha_emision: 'Fecha de emisión',
  fecha_caducidad: 'Fecha de caducidad',
  estado_civil: 'Estado civil',
  sexo: 'Sexo',
  ubigeo: 'Ubigeo',
};

const ETIQUETA_MRZ: Record<string, string> = {
  doc_number: 'Número (MRZ)',
  surname: 'Apellidos (MRZ)',
  given_names: 'Prenombres (MRZ)',
  birth_date: 'Fecha de nacimiento (MRZ)',
  expiry_date: 'Fecha de caducidad (MRZ)',
  sex: 'Sexo (MRZ)',
  nationality: 'Nacionalidad (MRZ)',
  country: 'País emisor (MRZ)',
  doc_type: 'Tipo de documento (MRZ)',
  personal_number: 'Número personal (MRZ)',
};

const TEXTO_COTEJO: Record<CampoDelCotejo, string> = {
  coincide: 'Coincide',
  no_coincide: 'No coincide',
  no_comprobable: 'No comprobable',
};

function Bloque({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg p-3" style={{ background: 'var(--surface-2)' }}>
      <div className="mb-0.5 text-[12px]" style={{ color: 'var(--text-3)' }}>
        {titulo}
      </div>
      {children}
    </div>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-[12px]" style={{ color: 'var(--text-3)' }}>
        {etiqueta}
      </span>
      <span className="text-right text-[12.5px]">{valor}</span>
    </div>
  );
}

/** El fallback: lo que no está contemplado arriba, en crudo. Enseñar de más antes que ocultar. */
function Cruda({ datos }: { datos: Record<string, unknown> }) {
  if (Object.keys(datos).length === 0) return null;
  return (
    <pre
      className="mono overflow-auto rounded p-2 text-[10.5px] leading-snug"
      style={{ background: 'var(--surface)', color: 'var(--text-3)', maxHeight: 180 }}
    >
      {JSON.stringify(datos, null, 1)}
    </pre>
  );
}

/** Una cara del documento: OCR y MRZ como tabla etiqueta→valor. */
function LadoDoc({ titulo, lado }: { titulo: string; lado: LadoDeDocumento | null | undefined }) {
  if (!lado) return null;
  const ocr = lado.ocr_fields ?? {};
  const mrz = lado.mrz_fields ?? {};

  const filas: [string, string][] = [];
  for (const [clave, etiqueta] of Object.entries(ETIQUETA_OCR)) {
    const v = ocr[clave];
    if (typeof v === 'string' && v.trim() !== '') filas.push([etiqueta, v]);
  }
  for (const [clave, etiqueta] of Object.entries(ETIQUETA_MRZ)) {
    const v = mrz[clave];
    if (typeof v === 'string' && v.trim() !== '') filas.push([etiqueta, v]);
  }

  const resto: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(ocr)) if (!(k in ETIQUETA_OCR)) resto[k] = v;
  for (const [k, v] of Object.entries(mrz)) if (!(k in ETIQUETA_MRZ)) resto[`mrz.${k}`] = v;

  if (filas.length === 0 && Object.keys(resto).length === 0 && lado.mrz_valid === undefined) {
    return null;
  }

  return (
    <Bloque titulo={titulo}>
      {typeof lado.mrz_valid === 'boolean' ? (
        <Dato etiqueta="MRZ válido" valor={lado.mrz_valid ? 'Sí' : 'No'} />
      ) : null}
      {filas.map(([e, v]) => (
        <Dato key={e} etiqueta={e} valor={v} />
      ))}
      <Cruda datos={resto} />
    </Bloque>
  );
}

function Expediente({ s }: { s: SenalesDeExpediente }) {
  const identidad = s.identidad ?? null;
  const vida = s.vida ?? null;
  const pulso = s.pulso ?? null;
  const ruido = s.ruido ?? null;

  // Todo lo que el expediente traiga y esta pantalla no liste va al <pre> del final.
  const conocidas = new Set(['vida', 'pulso', 'ruido', 'identidad', 'documentoAnverso', 'documentoReverso']);
  const resto: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(s)) {
    if (!conocidas.has(k) && v !== null && v !== undefined) resto[k] = v;
  }

  return (
    <div className="flex flex-col gap-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
      <div className="text-[12px] font-semibold" style={{ color: 'var(--text-3)' }}>
        Expediente · vista interna
      </div>

      <LadoDoc titulo="Documento — anverso" lado={s.documentoAnverso} />
      <LadoDoc titulo="Documento — reverso" lado={s.documentoReverso} />

      {identidad ? (
        <Bloque titulo="Cotejo de identidad">
          {identidad.fuente ? (
            <Dato
              etiqueta="Fuente"
              valor={identidad.fuente === 'registro' ? 'Registro oficial (RENIEC)' : 'Declarado por el cliente'}
            />
          ) : null}
          {identidad.cotejo ? (
            <>
              <Dato etiqueta="Nombres" valor={TEXTO_COTEJO[identidad.cotejo.nombres]} />
              <Dato etiqueta="Apellidos" valor={TEXTO_COTEJO[identidad.cotejo.apellidos]} />
              <Dato etiqueta="Fecha de nacimiento" valor={TEXTO_COTEJO[identidad.cotejo.fechaNacimiento]} />
              <Dato
                etiqueta="Cotejo global"
                valor={
                  identidad.cotejo.global === 'coincide'
                    ? 'Coincide'
                    : identidad.cotejo.global === 'discrepante'
                      ? 'Discrepante'
                      : 'No comprobable'
                }
              />
            </>
          ) : null}
          {typeof identidad.registroConsultado === 'boolean' ? (
            <Dato etiqueta="Registro consultado" valor={identidad.registroConsultado ? 'Sí' : 'No'} />
          ) : null}
          {typeof identidad.registroDisponible === 'boolean' && !identidad.registroDisponible ? (
            <Dato etiqueta="Registro disponible" valor="No (fuente caída)" />
          ) : null}
          {identidad.fuenteRegistro ? (
            <Dato
              etiqueta="Respuesta del registro"
              valor={identidad.fuenteRegistro === 'cache' ? 'Desde caché' : 'Consulta a dondato'}
            />
          ) : null}
          {typeof identidad.personaEncontrada === 'boolean' ? (
            <Dato etiqueta="Persona encontrada" valor={identidad.personaEncontrada ? 'Sí' : 'No'} />
          ) : null}
          {typeof identidad.fallecida === 'boolean' ? (
            <Dato etiqueta="Fallecida" valor={identidad.fallecida ? 'Sí' : 'No'} />
          ) : null}
          {identidad.tripleMatch ? (
            <>
              <Dato etiqueta="Triple match" valor={identidad.tripleMatch.passed ? 'Aprobado' : 'No aprobado'} />
              <Dato etiqueta="Selfie ↔ documento" valor={String(identidad.tripleMatch.match_selfie_doc)} />
              <Dato etiqueta="Documento ↔ registro" valor={String(identidad.tripleMatch.match_doc_gov)} />
              <Dato etiqueta="Selfie ↔ registro" valor={String(identidad.tripleMatch.match_selfie_gov)} />
              <Dato etiqueta="Puntaje compuesto" valor={String(identidad.tripleMatch.composite_score)} />
            </>
          ) : null}
        </Bloque>
      ) : null}

      {vida || pulso || ruido ? (
        <Bloque titulo="Prueba de vida">
          {vida ? <Dato etiqueta="Estado" valor={vida.estado.replace(/_/g, ' ')} /> : null}
          {vida && vida.motivos.length > 0 ? (
            <Dato etiqueta="Motivos" valor={vida.motivos.map((m) => m.replace(/_/g, ' ')).join(', ')} />
          ) : null}
          {pulso ? (
            <>
              {typeof pulso.bpm === 'number' ? <Dato etiqueta="Pulso (bpm)" valor={String(pulso.bpm)} /> : null}
              <Dato etiqueta="SNR" valor={String(pulso.snr)} />
              <Dato etiqueta="Calidad" valor={pulso.quality} />
            </>
          ) : null}
          {ruido ? (
            <>
              <Dato etiqueta="Ruido del sensor (intra_corr)" valor={String(ruido.intra_corr)} />
              <Dato etiqueta="Ruido consistente" valor={ruido.consistent ? 'Sí' : 'No'} />
            </>
          ) : null}
        </Bloque>
      ) : null}

      {Object.keys(resto).length > 0 ? (
        <Bloque titulo="Resto de señales">
          <Cruda datos={resto} />
        </Bloque>
      ) : null}
    </div>
  );
}

export function Verificaciones({ resumen }: { resumen: Resumen | null }) {
  const [filas, setFilas] = useState<FilaHistorial[] | null>(null);
  const [sel, setSel] = useState<Verificacion | null>(null);
  const [doc, setDoc] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<ErrorApi | null>(null);

  useEffect(() => {
    api.historial().then(setFilas).catch(() => setFilas([]));
  }, []);

  async function verificar() {
    if (!/^\d{8}$/.test(doc)) {
      setError(new ErrorApi(400, 'El DNI tiene que ser de 8 dígitos.', false));
      return;
    }
    setOcupado(true);
    setError(null);
    try {
      const v = await api.verificar(doc);
      setSel(v);
      setDoc('');
      setFilas(await api.historial());
    } catch (e) {
      setError(e instanceof ErrorApi ? e : new ErrorApi(500, 'Algo falló.', false));
    } finally {
      setOcupado(false);
    }
  }

  const grupos = ORDEN.map((estado) => ({
    estado,
    ...GRUPO[estado],
    filas: (filas ?? []).filter((f) => f.estado === estado),
  })).filter((g) => g.filas.length > 0);

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* ── Área clara: el trabajo ─────────────────────────────────────────── */}
      <main className="flex flex-1 flex-col overflow-auto px-8 py-7">
        <header className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-[19px] font-bold tracking-[-0.01em]">Verificaciones</h1>
            <p className="mt-0.5 text-[13px]" style={{ color: 'var(--text-2)' }}>
              Selecciona una persona para ver su perfil.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={doc}
              onChange={(e) => setDoc(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="DNI de 8 dígitos"
              inputMode="numeric"
              className="tabular w-[170px] rounded-lg px-3 py-2 text-[13px] outline-none"
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border-2)',
                color: 'var(--text)',
              }}
              onKeyDown={(e) => e.key === 'Enter' && verificar()}
            />
            <Boton variante="primario" onClick={verificar} disabled={ocupado}>
              {ocupado ? 'Verificando…' : 'Verificar persona'}
            </Boton>
          </div>
        </header>

        {error ? (
          <div className="mb-4">
            <AvisoError mensaje={error.message} reembolsado={error.reembolsado} />
          </div>
        ) : null}

        {/* El candado. Que lo vea es transparencia; que no lo mueva es el producto. */}
        {resumen ? (
          <div
            className="mb-5 flex flex-wrap items-center gap-2 rounded-lg px-3.5 py-2.5 text-[12.5px]"
            style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}
          >
            <span aria-hidden>🔒</span>
            <span>
              Perfil de exposición:{' '}
              <strong style={{ color: 'var(--text)' }}>
                {TEXTO_EXPOSICION[resumen.exposicion]}
              </strong>{' '}
              — lo define Valídate según tu sector y tu marco legal.
            </span>
          </div>
        ) : null}

        {filas === null ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : filas.length === 0 ? (
          <Tarjeta>
            <Vacio
              titulo="Todavía no verificaste a nadie"
              texto="Cuando envíes a tu primera persona, aparecerá aquí agrupada por veredicto."
              accion={
                <Boton variante="primario" onClick={() => setDoc('')}>
                  Verificar a mi primera persona
                </Boton>
              }
            />
          </Tarjeta>
        ) : (
          <div className="flex flex-col gap-6">
            {grupos.map((g) => (
              <section key={g.estado}>
                <div className="mb-2 flex items-center gap-2.5">
                  <Pastilla estado={g.estado} />
                  <span className="tabular text-[13px]" style={{ color: 'var(--text-2)' }}>
                    {g.filas.length}
                  </span>
                  {g.hint ? (
                    <span className="text-[12px]" style={{ color: 'var(--text-3)' }}>
                      · {g.hint}
                    </span>
                  ) : null}
                </div>

                <Tarjeta className="overflow-hidden">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr style={{ color: 'var(--text-3)' }}>
                        <th className="px-4 py-2 text-left text-[12px] font-normal">Persona</th>
                        <th className="px-4 py-2 text-left text-[12px] font-normal">Referencia</th>
                        <th className="px-4 py-2 text-left text-[12px] font-normal">Fecha</th>
                        <th className="px-4 py-2 text-right text-[12px] font-normal">Veredicto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.filas.map((f) => (
                        <tr
                          key={f.id}
                          onClick={() => api.verificacion(f.id).then(setSel).catch(() => undefined)}
                          className="cursor-pointer"
                          style={{ borderTop: '1px solid var(--border)' }}
                        >
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2.5">
                              <Avatar nombre={f.nombre ?? '··'} />
                              <div className="min-w-0">
                                <div className="truncate">
                                  {/* Sin nombre = la fuente no lo encontró. Decirlo así y no
                                      con un guion: "no existe en RENIEC" es información. */}
                                  {f.nombre ?? 'No encontrada en la fuente'}
                                </div>
                                <div className="mono text-[11px]" style={{ color: 'var(--text-3)' }}>
                                  DNI {f.documento ?? '····'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5" style={{ color: 'var(--text-2)' }}>
                            {f.externalRef ?? '—'}
                          </td>
                          <td className="tabular px-4 py-2.5" style={{ color: 'var(--text-2)' }}>
                            {fecha(f.creadaEn)}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <Pastilla estado={f.estado} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Tarjeta>
              </section>
            ))}
          </div>
        )}
      </main>

      {/* ── Panel derecho: el perfil ───────────────────────────────────────── */}
      <aside
        className="w-[360px] shrink-0 overflow-auto px-6 py-7"
        style={{ background: 'var(--surface)', borderLeft: '1px solid var(--border)' }}
      >
        <h2 className="mb-4 text-[13px] font-semibold" style={{ color: 'var(--text-2)' }}>
          Perfil de verificación
        </h2>

        {!sel ? (
          <p className="text-[13px]" style={{ color: 'var(--text-3)' }}>
            El perfil de cada persona aparecerá aquí cuando selecciones una verificación.
          </p>
        ) : (
          <div className="flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <Avatar
                nombre={
                  sel.persona ? `${sel.persona.nombres} ${sel.persona.apellidoPaterno}` : '··'
                }
              />
              <div className="min-w-0">
                <div className="truncate text-[14px] font-semibold">
                  {sel.persona
                    ? `${sel.persona.nombres} ${sel.persona.apellidoPaterno} ${sel.persona.apellidoMaterno}`
                    : 'Sin datos de la fuente'}
                </div>
                <div className="mono text-[12px]" style={{ color: 'var(--text-3)' }}>
                  {sel.persona ? `DNI ${sel.persona.documento}` : '—'}
                </div>
              </div>
            </div>

            <Pastilla estado={sel.estado} />

            {/*
              Estos bloques solo se pintan si el SERVIDOR los mandó — es decir, si el
              perfil de exposición del cliente los permite (o si el actor es ADMIN, para
              el expediente). El front no decide qué mostrar: dibuja lo que llegó.
            */}
            {sel.riesgo ? (
              <div className="rounded-lg p-3" style={{ background: 'var(--surface-2)' }}>
                <div className="text-[12px]" style={{ color: 'var(--text-3)' }}>
                  Nivel de riesgo
                </div>
                <div className="text-[15px] font-semibold capitalize">{sel.riesgo}</div>
              </div>
            ) : null}

            {sel.motivos?.length ? (
              <div className="rounded-lg p-3" style={{ background: 'var(--surface-2)' }}>
                <div className="mb-1.5 text-[12px]" style={{ color: 'var(--text-3)' }}>
                  Motivos
                </div>
                <ul className="flex flex-col gap-1">
                  {sel.motivos.map((m) => (
                    <li key={m.codigo} className="text-[13px]">
                      {m.texto}
                      {/* El código crudo solo cuando vino el expediente: el admin audita
                          contra el catálogo del core, el cliente lee la frase y punto. */}
                      {sel.reasons ? (
                        <span className="mono ml-1.5 text-[10.5px]" style={{ color: 'var(--text-3)' }}>
                          {m.codigo}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div>
              <div className="mb-1.5 text-[12px]" style={{ color: 'var(--text-3)' }}>
                Fuentes consultadas
              </div>
              <div className="text-[13px]">RENIEC</div>
            </div>

            {/* El expediente entero: solo existe en el objeto cuando el actor es ADMIN. */}
            {sel.signals ? <Expediente s={sel.signals} /> : null}

            <div style={{ borderTop: '1px solid var(--border)' }} className="pt-3">
              {/*
                Si no sabemos el costo, la fila NO se pinta. Un "S/ 0.00" afirmaría que la
                consulta fue gratis — y sería falso. Callar es honesto; inventar, no.
              */}
              {sel.costo !== null ? (
                <div className="flex items-baseline justify-between">
                  <span className="text-[13px]" style={{ color: 'var(--text-2)' }}>
                    Costo
                  </span>
                  <span className="tabular text-[14px] font-semibold">
                    {resumen?.moneda === 'PEN' ? 'S/ ' : ''}
                    {sel.costo}
                  </span>
                </div>
              ) : null}
              <div className="tabular mt-1 text-[12px]" style={{ color: 'var(--text-3)' }}>
                {fecha(sel.creadaEn)}
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
